import React, { useEffect } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { ReactComponent as SearchIcon } from "assets/icons/search-white.svg";
import { Alert, Pagination } from "@material-ui/lab";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { SearchButton } from "components/searchButton";
import { Wrapper } from "components/wrapper";
import CustomBackdrop from "components/backdrop";
import { UltimaAtualizacao } from "components/ultimaAtualizacao";
import { ExportButtons } from "components/exportButtons";
import { SortDirectionSelect } from "components/sortDirectionSelect";
import { useLicitacoesState } from "./context/licitacoes-context";
import { useLicitacoesController } from "./context/licitacoes-controller";
import { LicitacoesTable } from "./licitacoesTable.component";

export function LicitacoesPage() {
  const { licitacoes, licitacoesPagedRequest, loading, orgaos, modalidades, fases, situacoes, ultimaAtualizacao, error } = useLicitacoesState();
  const { onInit, handleOnChange, listLicitacoes, exportLicitacoes } = useLicitacoesController();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      await onInit();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageClick = (event: React.ChangeEvent<unknown>, value: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      await listLicitacoes({ ...licitacoesPagedRequest, page: value - 1 }, true);
    })();
  };

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Licitações" },
        ]}
      />
      <PageTitle>Licitações</PageTitle>
      <Wrapper>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  variant="outlined"
                  fullWidth
                  name="text-field-term"
                  label="Número ou objeto"
                  value={licitacoesPagedRequest?.term || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Órgão</InputLabel>
                  <Select
                    name="select-orgao-id"
                    value={licitacoesPagedRequest?.idOrgao || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Órgão"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {orgaos?.map((o) => (
                      <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Modalidade</InputLabel>
                  <Select
                    name="select-modalidade-id"
                    value={licitacoesPagedRequest?.idModalidadeLicitacao || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Modalidade"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {modalidades?.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Fase</InputLabel>
                  <Select
                    name="select-fase-id"
                    value={licitacoesPagedRequest?.idFaseLicitacao || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Fase"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {fases?.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Situação</InputLabel>
                  <Select
                    name="select-situacao-id"
                    value={licitacoesPagedRequest?.idSituacaoLicitacao || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Situação"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {situacoes?.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <SearchButton onClick={() => (async () => {
                  await listLicitacoes(licitacoesPagedRequest, true);
                })()}>
                  <SearchIcon /> Consultar
                </SearchButton>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="date"
                  name="date-field-inicio"
                  label="Data início"
                  value={licitacoesPagedRequest?.dataInicio || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  variant="outlined"
                  fullWidth
                  type="date"
                  name="date-field-fim"
                  label="Data fim"
                  value={licitacoesPagedRequest?.dataFim || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <SortDirectionSelect
                  value={licitacoesPagedRequest?.orderDirection}
                  onChange={(e) => handleOnChange(e)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <CustomBackdrop open={loading} />
            <UltimaAtualizacao data={ultimaAtualizacao} />
            <ExportButtons onExport={exportLicitacoes} />
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <LicitacoesTable currentItems={licitacoes} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3} justifyContent="center">
              <Pagination
                size="large"
                variant="outlined"
                shape="rounded"
                onChange={handlePageClick}
                count={licitacoes?.pageCount}
                color="primary"
              />
            </Grid>
          </Grid>
        </Grid>
      </Wrapper>
    </>
  );
}
